<template>
  <div class="row">
    <div class="col-md-12">
      <div class="category-top-bar">
        <div class="top-bar">
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="goBack">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </div>
      </div>
      <p class="clearfix"></p>

      <div class="card automation-beneficiaries">
        <div class="card-content">
          <!-- Header -->
          <div class="ab-head">
            <span class="ab-head__icon"><i :class="isCart ? 'fa fa-shopping-cart' : 'fa fa-birthday-cake'"></i></span>
            <div>
              <h4 class="ab-head__title">{{ $t('marketingAutomation.beneficiariesTitle') }}</h4>
              <div class="ab-head__sub" v-if="campaign">{{ campaign.name }}</div>
            </div>
          </div>

          <!-- Status chips = filters -->
          <div class="ab-chips">
            <a v-for="chip in chips"
               :key="chip.key"
               class="ab-chip"
               :class="['ab-chip--' + chip.key, { 'is-active': filters.status === chip.value }]"
               @click.prevent="setStatus(chip.value)">
              <span class="ab-chip__count">{{ counts[chip.key] || 0 }}</span>
              <span class="ab-chip__label"><i :class="chip.icon"></i> {{ $t(chip.label) }}</span>
            </a>
          </div>

          <!-- Search + year -->
          <div class="ab-filters">
            <el-input v-model="filters.search"
                      class="ab-search"
                      prefix-icon="el-icon-search"
                      clearable
                      :placeholder="$t('marketingAutomation.searchCustomer')"
                      @input="onSearch"></el-input>
            <el-select v-model="filters.year" class="ab-year" @change="reload">
              <el-option :label="$t('marketingAutomation.allYears')" value=""></el-option>
              <el-option v-for="year in years" :key="year" :label="String(year)" :value="year"></el-option>
            </el-select>
          </div>

          <!-- List -->
          <!-- rendered once the campaign type is known (its columns differ) -->
          <el-table class="ab-table"
                    v-if="campaign"
                    v-loading="loading"
                    :data="items"
                    :empty-text="$t('marketingAutomation.noBeneficiaries')"
                    style="width: 100%">
            <el-table-column :min-width="220" :label="$t('marketingAutomation.customer')">
              <template slot-scope="props">
                <div class="ab-customer">
                  <span class="ab-avatar">{{ initials(props.row.customer) }}</span>
                  <div class="ab-customer__text">
                    <div class="ab-strong">{{ (props.row.customer && props.row.customer.name) || '—' }}</div>
                    <div class="ab-muted">{{ contact(props.row.customer) }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column v-if="!isCart" :min-width="140" :label="$t('marketingAutomation.birthday')">
              <template slot-scope="props">
                <div class="ab-strong">{{ formatDay(props.row.occasion_date) }}</div>
                <div class="ab-muted">{{ $t('marketingAutomation.yearLabel', { year: props.row.year }) }}</div>
              </template>
            </el-table-column>
            <el-table-column v-else :min-width="150" :label="$t('marketingAutomation.cart')">
              <template slot-scope="props">
                <template v-if="props.row.cart">
                  <div class="ab-strong">
                    #{{ props.row.cart.id }}
                    <span class="ab-order-total" v-if="props.row.cart.total !== null">· {{ props.row.cart.total }} {{ currency }}</span>
                  </div>
                  <div class="ab-muted">{{ formatDateTime(props.row.cart.created_at) }}</div>
                </template>
                <span v-else class="ab-muted">—</span>
              </template>
            </el-table-column>

            <el-table-column :min-width="210" :label="$t('marketingAutomation.notifications')">
              <template slot-scope="props">
                <div class="ab-push" :class="'ab-push--' + pushState(props.row.announce)">
                  <i :class="pushIcon(props.row.announce)"></i>
                  <span class="ab-push__kind">{{ isCart ? $t('marketingAutomation.cartPush1Short') : $t('marketingAutomation.announcePushShort') }}:</span>
                  {{ pushText(props.row.announce) }}
                </div>
                <div class="ab-push" :class="'ab-push--' + pushState(props.row.day)">
                  <i :class="pushIcon(props.row.day)"></i>
                  <span class="ab-push__kind">{{ isCart ? $t('marketingAutomation.cartPush2Short') : $t('marketingAutomation.dayPushShort') }}:</span>
                  {{ pushText(props.row.day) }}
                </div>
              </template>
            </el-table-column>

            <el-table-column :min-width="130" :label="$t('marketingAutomation.status')">
              <template slot-scope="props">
                <span class="ab-status" :class="'ab-status--' + props.row.status">
                  <span class="ab-status__dot"></span>{{ statusLabel(props.row.status) }}
                </span>
                <div class="ab-muted" v-if="isCart && props.row.status === 'granted' && props.row.valid_until">
                  {{ $t('marketingAutomation.validUntil', { time: formatDateTime(props.row.valid_until) }) }}
                </div>
              </template>
            </el-table-column>

            <el-table-column :min-width="170" :label="$t('marketingAutomation.order')">
              <template slot-scope="props">
                <template v-if="props.row.status === 'used'">
                  <div class="ab-strong" v-if="props.row.order">
                    #{{ props.row.order.number || props.row.order.id }}
                    <span class="ab-order-total" v-if="props.row.order.total !== null">· {{ props.row.order.total }} {{ currency }}</span>
                  </div>
                  <div class="ab-muted">{{ formatDateTime(props.row.used_at) }}</div>
                </template>
                <!-- forgotten cart ordered after the push (with or without coupon) -->
                <template v-else-if="props.row.converted">
                  <div class="ab-strong">#{{ props.row.converted.number || props.row.converted.order_id }}</div>
                  <div class="ab-muted">{{ $t('marketingAutomation.cartPlaced') }} · {{ formatDateTime(props.row.converted.at) }}</div>
                </template>
                <span v-else class="ab-muted">—</span>
                <span v-if="props.row.status === 'used' && props.row.converted" class="ab-recovered"><i class="fa fa-shopping-bag"></i> {{ $t('marketingAutomation.status_converted') }}</span>
              </template>
            </el-table-column>
          </el-table>

          <div class="ab-pagination" v-if="pagination.total > 0">
            <el-pagination background
                           layout="total, sizes, prev, pager, next"
                           :total="pagination.total"
                           :page-size="pagination.per_page"
                           :page-sizes="[20, 50, 100]"
                           :current-page="pagination.current_page"
                           @size-change="onSizeChange"
                           @current-change="onPageChange">
            </el-pagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Input, Select, Option, Table, TableColumn, Pagination} from 'element-ui'

  Vue.use(Input)
  Vue.use(Select)
  Vue.use(Option)
  Vue.use(Table)
  Vue.use(TableColumn)
  Vue.use(Pagination)

  // must match AutomationCampaignGrant::PUSH_* (API)
  const PUSH_SENT = 1
  const PUSH_NO_DEVICE = 2
  const PUSH_FAILED = 3

  export default {
    props: {
      route: {
        type: Object,
        default: () => ({})
      }
    },
    data () {
      return {
        loading: false,
        currency: '',
        campaign: null,
        items: [],
        counts: {},
        years: [],
        filters: {
          status: null,
          search: '',
          year: ''
        },
        pagination: {
          total: 0,
          per_page: 20,
          current_page: 1,
          last_page: 1
        },
        searchTimer: null
      }
    },
    computed: {
      // must match AutomationCampaign::TYPE_ABANDONED_CART
      isCart () {
        return !!(this.campaign && this.campaign.type === 3)
      },
      // status cards = filters (per campaign type)
      chips () {
        if (this.isCart) {
          return [
            { key: 'all', value: null, label: 'marketingAutomation.statusAllSent', icon: 'fa fa-paper-plane' },
            { key: 'granted', value: 'granted', label: 'marketingAutomation.cartStatusGranted', icon: 'fa fa-gift' },
            { key: 'notified', value: 'notified', label: 'marketingAutomation.status_notified', icon: 'fa fa-bell-o' },
            { key: 'used', value: 'used', label: 'marketingAutomation.cartStatusUsed', icon: 'fa fa-check-circle' },
            { key: 'converted', value: 'converted', label: 'marketingAutomation.status_converted', icon: 'fa fa-shopping-bag' },
            { key: 'expired', value: 'expired', label: 'marketingAutomation.status_expired', icon: 'fa fa-clock-o' }
          ]
        }
        return [
          { key: 'all', value: null, label: 'marketingAutomation.statusAll', icon: 'fa fa-users' },
          { key: 'granted', value: 'granted', label: 'marketingAutomation.status_granted', icon: 'fa fa-gift' },
          { key: 'used', value: 'used', label: 'marketingAutomation.status_used', icon: 'fa fa-check-circle' },
          { key: 'expired', value: 'expired', label: 'marketingAutomation.status_expired', icon: 'fa fa-clock-o' }
        ]
      },
      campaignId () {
        const query = (this.route && this.route.query) || {}
        return parseInt(query.campaign_id, 10) || null
      }
    },
    created () {
      const companyData = this.$session.get('selectedCompanyData') || {}
      this.currency = this.getCurrencySymbol(companyData.currency_iso)
      this.load()
    },
    beforeDestroy () {
      clearTimeout(this.searchTimer)
    },
    methods: {
      load () {
        if (!this.campaignId) {
          return
        }
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany'),
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        }
        if (this.filters.status) {
          data.status = this.filters.status
        }
        if (this.filters.year) {
          data.year = this.filters.year
        }
        if ((this.filters.search || '').trim()) {
          data.search = this.filters.search.trim()
        }
        this.loading = true
        this.apiRequest('/automation-campaign/' + this.campaignId + '/grants', 'GET', data, (response) => {
          this.loading = false
          if (response.status !== true || !response.data) {
            return
          }
          this.campaign = response.data.campaign
          this.items = response.data.items || []
          this.counts = response.data.counts || {}
          this.years = response.data.years || []
          this.pagination = { ...this.pagination, ...(response.data.pagination || {}) }
        })
      },
      reload () {
        this.pagination.current_page = 1
        this.load()
      },
      setStatus (value) {
        if (this.filters.status === value) {
          return
        }
        this.filters.status = value
        this.reload()
      },
      onSearch () {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => this.reload(), 400)
      },
      onPageChange (page) {
        this.pagination.current_page = page
        this.load()
      },
      onSizeChange (size) {
        this.pagination.per_page = size
        this.reload()
      },
      statusLabel (status) {
        if (this.isCart && status === 'granted') {
          return this.$t('marketingAutomation.cartStatusGranted')
        }
        if (this.isCart && status === 'used') {
          return this.$t('marketingAutomation.cartStatusUsed')
        }
        return this.$t('marketingAutomation.status_' + status)
      },
      initials (customer) {
        const name = ((customer && customer.name) || '?').trim()
        return name.split(/\s+/).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('')
      },
      contact (customer) {
        if (!customer) {
          return ''
        }
        return [customer.phone, customer.email].filter(Boolean).join(' · ')
      },
      formatDay (value) {
        if (!value) {
          return '—'
        }
        const parts = String(value).substr(0, 10).split('-')
        return parts.length === 3 ? parts[2] + '.' + parts[1] + '.' + parts[0] : value
      },
      formatDateTime (value) {
        if (!value) {
          return ''
        }
        return this.formatDay(value) + ' ' + String(value).substr(11, 5)
      },
      pushState (push) {
        const status = push && push.status
        if (status === PUSH_SENT) {
          return 'sent'
        }
        if (status === PUSH_NO_DEVICE) {
          return 'nodevice'
        }
        if (status === PUSH_FAILED) {
          return 'failed'
        }
        return 'none'
      },
      pushIcon (push) {
        return {
          sent: 'fa fa-check',
          nodevice: 'fa fa-bell-slash-o',
          failed: 'fa fa-exclamation-triangle',
          none: 'fa fa-minus'
        }[this.pushState(push)]
      },
      pushText (push) {
        const state = this.pushState(push)
        if (state === 'none') {
          return this.$t('marketingAutomation.pushNotSent')
        }
        const label = this.$t('marketingAutomation.push_' + state)
        return push.at ? label + ' · ' + this.formatDateTime(push.at) : label
      },
      goBack () {
        this.$bus.$emit('refresh')
        this.$bus.$emit('close-modal')
      }
    }
  }
</script>
<style>
  .automation-beneficiaries .card-content {
    padding: 20px 24px;
  }
  .ab-head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
  }
  .ab-head__icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #f3eefb;
    color: #7e57c2;
    font-size: 20px;
  }
  .ab-head__title {
    margin: 0;
    font-weight: 600;
  }
  .ab-head__sub {
    color: #8a8a9a;
    font-size: 13px;
  }
  /* chips */
  .ab-chips {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
    margin-bottom: 18px;
  }
  .ab-chip {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px 16px;
    border-radius: 12px;
    border: 1px solid #e6e6ef;
    background: #fff;
    cursor: pointer;
    color: #555;
    transition: border-color .15s, box-shadow .15s;
  }
  .ab-chip:hover {
    text-decoration: none;
    color: #555;
    border-color: #cfc3ea;
  }
  .ab-chip.is-active {
    border-color: #7e57c2;
    box-shadow: 0 0 0 1px #7e57c2 inset;
    background: #faf7fe;
  }
  .ab-chip__count {
    font-size: 22px;
    font-weight: 700;
    color: #2c2c3a;
    line-height: 1.2;
  }
  .ab-chip__label {
    font-size: 12px;
  }
  .ab-chip--granted .fa { color: #1565c0; }
  .ab-chip--used .fa { color: #2e7d32; }
  .ab-chip--expired .fa { color: #9e9e9e; }
  .ab-chip--all .fa { color: #7e57c2; }
  /* filters */
  .ab-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
  }
  .ab-search {
    max-width: 340px;
  }
  .ab-year {
    width: 150px;
  }
  /* table: the global `.el-table .cell { height: 50px !important }` would clip 2 lines */
  .ab-table tbody td .cell {
    height: 58px !important;
    display: flex !important;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    text-align: left;
    white-space: normal;
    line-height: 1.35;
    overflow: hidden;
    padding-left: 14px;
  }
  .ab-table th .cell {
    padding-left: 14px;
  }
  .ab-table tbody tr {
    cursor: default;
  }
  .ab-customer {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .ab-customer__text {
    min-width: 0;
  }
  .ab-avatar {
    flex: 0 0 34px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ede7f6;
    color: #5e35b1;
    font-size: 12px;
    font-weight: 700;
  }
  .ab-strong {
    font-weight: 600;
    color: #2c2c3a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ab-muted {
    font-size: 12px;
    color: #8a8a9a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ab-push {
    font-size: 12px;
    white-space: nowrap;
    color: #8a8a9a;
  }
  .ab-push .fa {
    width: 14px;
  }
  .ab-push__kind {
    color: #555;
    font-weight: 600;
  }
  .ab-push--sent .fa { color: #2e7d32; }
  .ab-push--nodevice .fa { color: #9e9e9e; }
  .ab-push--failed .fa { color: #e53935; }
  .ab-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    border: 1px solid #e0e0e0;
    background: #f5f5f5;
    color: #777;
  }
  .ab-status__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .ab-status--granted {
    background: #e3f2fd;
    color: #1565c0;
    border-color: #bbdefb;
  }
  .ab-status--notified {
    background: #f3eefb;
    color: #5e35b1;
    border-color: #d1c4e9;
  }
  .ab-recovered {
    display: inline-block;
    margin-top: 2px;
    font-size: 11px;
    color: #2e7d32;
  }
  .ab-chip--notified .fa { color: #7e57c2; }
  .ab-chip--converted .fa { color: #2e7d32; }
  .ab-status--used {
    background: #eef7ee;
    color: #2e7d32;
    border-color: #c8e6c9;
  }
  .ab-order-total {
    font-weight: 400;
    color: #555;
  }
  .ab-pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/AutomationCampaigns/AutomationBeneficiariesPage.vue